/**
 * 全局 antd 实例持有器
 *
 * 解决 antd v5 静态方法（message.success / Modal.confirm 等）无法消费
 * React Context（动态主题、国际化等）的问题。
 *
 * 使用方式：
 * 1. 在 <App> 内渲染 <GlobalAntdHolder />（已在 app.tsx 中完成）
 * 2. 组件内优先使用 App.useApp()
 * 3. 非组件代码（工具函数、请求拦截器等）使用本文件导出的 getMessageApi() 等
 *
 * **SSR / 微前端安全**: 组件卸载时自动清理全局引用，避免实例污染。
 */
import { App } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { ModalStaticFunctions } from 'antd/es/modal/confirm';
import type { NotificationInstance } from 'antd/es/notification/interface';
import type React from 'react';
import { useEffect } from 'react';

let messageApi: MessageInstance | null = null;
let notificationApi: NotificationInstance | null = null;
let modalApi: Omit<ModalStaticFunctions, 'warn'> | null = null;

/** 获取全局 message 实例（上下文感知） */
export const getMessageApi = (): MessageInstance | null => messageApi;

/** 获取全局 notification 实例（上下文感知） */
export const getNotificationApi = (): NotificationInstance | null =>
  notificationApi;

/** 获取全局 modal 实例（上下文感知） */
export const getModalApi = (): Omit<ModalStaticFunctions, 'warn'> | null =>
  modalApi;

/**
 * 重置全局实例引用（卸载 / 测试场景使用）
 */
export function resetGlobalAntdApis(): void {
  messageApi = null;
  notificationApi = null;
  modalApi = null;
}

/**
 * 全局 antd 实例挂载组件
 *
 * 必须放置在 <App> 内部。捕获上下文感知的 message/notification/modal 实例，
 * 使非组件代码也能使用动态主题感知的 antd API。
 *
 * 组件卸载时自动清理全局引用，防止 SSR / 微前端场景下的实例污染。
 */
export const GlobalAntdHolder: React.FC = () => {
  const { message, notification, modal } = App.useApp();

  useEffect(() => {
    messageApi = message;
    notificationApi = notification;
    modalApi = modal;

    return () => {
      messageApi = null;
      notificationApi = null;
      modalApi = null;
    };
  }, [message, notification, modal]);

  return null;
};
